// src/components/VerticalNavbar.js
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  BookOpen,
  Clapperboard,
  Coins,
  Compass,
  LogOut,
  MessageSquare,
  Radio,
  Sparkles,
  User,
  Wallet,
  Wand2,
  ImagePlus,
} from 'lucide-react';

import { useWallet } from '../context/WalletContext';
import { useToast } from './Toast';
import PostComposer from './kolTabs/PostComposer';
import { landAfterPost } from '../lib/feedScroll';
import { signInLabel } from '../config/emailSignIn';
import { useUnreadChats } from '../hooks/useUnreadChats';
import HoverCard from './ui/hover-card';
import AccountCardContent from './AccountHoverCard';
import { clearBalanceCache } from '../hooks/useBalanceSummary';
import logo from '../assets/logo.png';

// DESKTOP ONLY (md and up). Under md this rail is `hidden` and
// MobileTabBar takes over — see components/MobileTabBar.js. The two share
// the item definitions exported below rather than keeping parallel lists,
// because a nav that exists twice is a nav that drifts.
//
// Note the hover-expand below (onMouseEnter/onMouseLeave): that is exactly
// why this component cannot simply be narrowed for phones. With no hover
// it would sit collapsed at w-14 forever, showing icons with no labels.

// Always visible
export const primaryNavItems = [
  {
    id: 'discover',
    label: 'Discover',
    icon: Compass,
    path: '/discover',
  },
  {
    id: 'livestreams',
    label: 'Livestreams',
    icon: Radio,
    path: '/discover?tab=live',
  },
];

// Visible only to approved creators
export const creatorNavItems = [
  {
    id: 'studio',
    label: 'Studio',
    icon: Clapperboard,
    path: '/studio',
  },
  {
    id: 'create',
    label: 'Create',
    icon: Wand2,
    path: '/create',
  },
];

// 031-03 / 031-04: an ACTION (like connectItem), not a route — opens the post
// composer. Approved creators see it LAST, in Create Model's lime (highlight),
// in the slot Create Model holds for everyone else.
export const postItem = {
  id: 'post',
  label: 'Post',
  icon: ImagePlus,
  action: 'post',
  highlight: true,
};

// Always visible
export const secondaryNavItems = [
  {
    id: 'chats',
    label: 'Chats',
    icon: MessageSquare,
    path: '/chats',
  },
  {
    id: 'rewards',
    label: 'Rewards',
    icon: Coins,
    path: '/rewards',
  },
  {
    id: 'docs',
    label: 'How it works',
    icon: BookOpen,
    path: '/docs',
  },
];

export const profileItem = {
  id: 'profile',
  label: 'Profile',
  icon: User,
  path: '/profile',
};

// Connecting is an ACTION, not a route, so it carries `action` instead of
// `path`. Defined here with the other nav items so MobileTabBar can import
// it rather than copy a label and an icon — this rail renders its own
// bespoke white CTA for the same thing (see renderAccountSection) because a
// conversion button is not a nav row.
export const connectItem = {
  id: 'connect',
  label: 'Connect',
  icon: Wallet,
  action: 'connect',
};

export const createModelItem = {
  id: 'create-model',
  label: 'Create Model',
  icon: Sparkles,
  path: '/create-model',
  highlight: true,
};

const VerticalNavbar = ({
  onToggle,
  currentUser,
  creatorProfile = null,
  isApprovedCreator = false,
}) => {
  // Unified account identity for the bottom block. Creators' avatar and
  // handle live on the creators row (their users row is often a bare
  // provisioned stub), so prefer the creator profile when present —
  // previously creators fell into the generic "Connected" wallet icon.
  const accountIdentity = (() => {
    if (creatorProfile && (creatorProfile.avatar_url || creatorProfile.handle || creatorProfile.name)) {
      return {
        avatarUrl: creatorProfile.avatar_url || currentUser?.avatar_url || null,
        name: creatorProfile.handle || creatorProfile.name || currentUser?.username || 'creator',
      };
    }
    if (currentUser) {
      return {
        avatarUrl: currentUser.avatar_url || null,
        name: currentUser.username || 'user',
      };
    }
    return null;
  })();
  const {
    isConnected,
    walletAddress,
    email,
    setShowWalletModal,
    disconnectWallet,
  } = useWallet();

  const location = useLocation();
  const navigate = useNavigate();

  // Start collapsed by default. The rail stays expanded while the account
  // card is open: the card lives in a portal, so moving the pointer into it
  // leaves the rail — that must not collapse the rail under the card.
  const [railHover, setRailHover] = useState(false);
  const [accountCardOpen, setAccountCardOpen] = useState(false);
  const isCollapsed = !(railHover || accountCardOpen);

  // Unread chats badge (realtime count + chime lives in the hook)
  const unreadChats = useUnreadChats(currentUser?.id);

  // Require both a connected wallet and an approved creator record.
  const canAccessCreatorTools =
    isConnected && isApprovedCreator;

  const navItems = useMemo(() => {
    return [
      ...primaryNavItems,
      ...(canAccessCreatorTools
        ? creatorNavItems
        : []),
      ...secondaryNavItems,
      // no Profile row: the account block at the bottom (avatar + name) opens /profile
      // 031-04: the last slot — Post for an approved creator, Create Model for everyone else
      canAccessCreatorTools ? postItem : createModelItem,
    ];
  }, [
    canAccessCreatorTools,
  ]);

  // Determine current page from URL path + search.
  const currentPage = useMemo(() => {
    const {
      pathname,
      search,
    } = location;

    const params = new URLSearchParams(search);
    const tab = params.get('tab');

    if (
      pathname === '/' ||
      pathname === '/discover'
    ) {
      if (tab === 'live') {
        return 'livestreams';
      }

      return 'discover';
    }

    if (pathname.startsWith('/kol/')) {
      return 'discover';
    }

    if (pathname.startsWith('/creator/')) {
      return 'discover';
    }

    if (pathname === '/create') {
      return 'create';
    }

    if (pathname === '/chats') {
      return 'chats';
    }

    if (pathname === '/rewards') {
      return 'rewards';
    }

    if (pathname === '/docs') {
      return 'docs';
    }

    if (
      pathname === '/studio' ||
      pathname.startsWith('/studio/')
    ) {
      return 'studio';
    }

    if (pathname === '/create-model') {
      return 'create-model';
    }

    if (pathname === '/profile') {
      return 'profile';
    }

    return 'discover';
  }, [location]);

  // Communicate collapse state to parent.
  useEffect(() => {
    onToggle?.(isCollapsed);
  }, [
    isCollapsed,
    onToggle,
  ]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  // 031-03: "Create Post" opens the composer (one instance, portaled by itself)
  const { showToast } = useToast();
  const [postOpen, setPostOpen] = useState(false);
  const postCreatorId = creatorProfile?.creator_id || creatorProfile?.id || null;
  // 031-10: a post lands on the creator's own Posts tab (lib/feedScroll landAfterPost)
  const handlePosted = () => {
    showToast({ type: 'success', title: 'Post published!' });
    landAfterPost({ creatorProfile, location, navigate });
  };
  const handleItem = (item) => {
    if (item.action === 'post') { setPostOpen(true); return; }
    handleNavigation(item.path);
  };

  const handleDisconnect = async () => {
    try {
      clearBalanceCache();
      await disconnectWallet?.();
      navigate('/discover');
    } catch (error) {
      console.error(
        'Disconnect failed:',
        error,
      );
    }
  };

  const truncateAddress = (address) => {
    if (!address) {
      return '';
    }

    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const renderAccountSection = () => {
    // Not connected
    if (!isConnected) {
      return (
        <button
          type="button"
          onClick={() => setShowWalletModal(true)}
          className={`
            flex w-full items-center rounded-xl
            bg-white text-black
            transition-all duration-200
            hover:bg-zinc-200
            ${isCollapsed
              ? 'justify-center px-1 py-3'
              : 'px-3 py-3'
            }
          `}
        >
          <Wallet
            className={
              !isCollapsed ? 'mr-3' : ''
            }
            size={20}
            strokeWidth={2}
          />

          {!isCollapsed && (
            <span className="text-sm font-semibold">
              {signInLabel('Connect Wallet')}
            </span>
          )}
        </button>
      );
    }

    // Connected wallet, but no user OR creator identity to show
    if (!accountIdentity) {
      return (
        <div className="space-y-2">
          <div
            className={`
              flex w-full items-center rounded-xl
              bg-zinc-800/50 py-2
              ${isCollapsed
                ? 'justify-center'
                : 'px-3'
              }
            `}
          >
            <div className="flex items-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#A3E635]">
                <Wallet
                  size={14}
                  className="text-black"
                />
              </div>

              {!isCollapsed && (
                <div className="ml-3 text-left">
                  <div className="text-sm font-medium text-white">
                    Connected
                  </div>

                  <div className="font-mono text-[10px] text-zinc-500">
                    {email || truncateAddress(walletAddress)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleDisconnect}
            className={`
              flex w-full select-none items-center rounded-xl
              py-3 text-zinc-500 outline-none
              transition-colors duration-200
              hover:bg-zinc-800/50 hover:text-red-400
              active:bg-zinc-800/70
              ${isCollapsed
                ? 'justify-center'
                : 'px-3'
              }
            `}
          >
            <LogOut
              className={
                !isCollapsed ? 'mr-3' : ''
              }
              size={22}
              strokeWidth={1.5}
            />

            {!isCollapsed && (
              <span className="text-sm font-medium">
                Disconnect
              </span>
            )}
          </button>
        </div>
      );
    }

    // Connected with an account. On a device that can hover, the avatar
    // also opens the account card (components/AccountHoverCard.js); a click
    // still goes to /profile, and touch devices only navigate.
    return (
      <div className="space-y-2">
        <HoverCard
          label="Account"
          onOpenChange={setAccountCardOpen}
          trigger={(cardProps) => (
        <button
          type="button"
          {...cardProps}
          onClick={() =>
            handleNavigation('/profile')
          }
          className={`
            group flex min-h-[44px] w-full items-center rounded-xl
            py-2 outline-none transition-all duration-200
            hover:bg-zinc-800/50 focus-visible:ring-2 focus-visible:ring-white/30
            ${isCollapsed
              ? 'justify-center px-1'
              : 'px-3'
            }
            ${currentPage === 'profile'
              ? 'bg-zinc-800/50'
              : ''
            }
          `}
        >
          <div className="flex items-center">
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-zinc-800 text-xs font-medium text-white">
                {accountIdentity.avatarUrl ? (
                  <img
                    src={accountIdentity.avatarUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  accountIdentity.name
                    ?.slice(0, 2)
                    .toUpperCase() || '??'
                )}
              </div>

              <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0F0F0F] bg-[#A3E635]" />
            </div>

            {!isCollapsed && (
              <div className="ml-3 text-left">
                <div className="text-sm font-medium text-white">
                  @{accountIdentity.name}
                </div>

                <div className="font-mono text-[10px] text-zinc-500">
                  {email || truncateAddress(walletAddress)}
                </div>
              </div>
            )}
          </div>
        </button>
          )}
        >
          {({ close }) => (
            <AccountCardContent
              identity={accountIdentity}
              walletAddress={walletAddress}
              email={email}
              isCreator={isApprovedCreator}
              onNavigate={close}
              onLogout={() => { close(); handleDisconnect(); }}
            />
          )}
        </HoverCard>

        <button
          type="button"
          onClick={handleDisconnect}
          className={`
            flex w-full items-center rounded-xl
            py-2 text-zinc-500
            transition-all duration-200
            hover:bg-zinc-800/50 hover:text-red-400
            ${isCollapsed
              ? 'justify-center px-1'
              : 'px-3'
            }
          `}
        >
          <LogOut
            className={
              !isCollapsed ? 'mr-3' : ''
            }
            size={18}
            strokeWidth={1.5}
          />

          {!isCollapsed && (
            <span className="text-sm">
              Log out
            </span>
          )}
        </button>
      </div>
    );
  };

  return (
    <div
      className={`
        fixed left-0 top-0 z-40
        hidden md:flex h-full flex-col
        border-r border-white/[0.04]
        bg-[#0F0F0F]/80 text-white
        backdrop-blur-xl
        transition-all duration-300
        ${isCollapsed
          ? 'w-14'
          : 'w-56'
        }
      `}
      onMouseEnter={() =>
        setRailHover(true)
      }
      onMouseLeave={() =>
        setRailHover(false)
      }
    >
      {/* Header */}
      <div
        className={`
          flex h-[72px] items-center px-3
          ${isCollapsed
            ? 'justify-center'
            : ''
          }
        `}
      >
        <button
          type="button"
          onClick={() =>
            handleNavigation('/discover')
          }
          className="flex cursor-pointer items-center outline-none transition-all hover:opacity-80 active:scale-[0.97]"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            <img
              src={logo}
              alt="Lunexx Logo"
              className="h-7 w-7 object-contain brightness-110"
            />
          </div>

          {!isCollapsed && (
            <span className="ml-3 text-base font-bold tracking-tight text-white">
              Lunexx
            </span>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const isActive =
              currentPage === item.id;
            const isHighlight =
              item.highlight;
            const badgeCount =
              item.id === 'chats' ? unreadChats : 0;

            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleItem(item)}
                  className={`
                    group flex w-full items-center
                    rounded-xl py-3
                    transition-all duration-200
                    ${isCollapsed
                      ? 'justify-center'
                      : 'px-3'
                    }
                    ${isActive
                      ? isHighlight
                        ? 'border border-[#A3E635]/20 bg-[#A3E635]/10 text-[#A3E635]'
                        : 'bg-zinc-800/50 text-white'
                      : isHighlight
                        ? 'border border-transparent text-[#A3E635]/70 hover:border-[#A3E635]/20 hover:bg-[#A3E635]/10 hover:text-[#A3E635]'
                        : 'text-zinc-300 hover:bg-zinc-800/50 hover:text-white'
                    }
                  `}
                >
                  {/* relative wrapper so the unread badge can pin to the
                      icon corner in BOTH collapsed and expanded states */}
                  <span
                    className={`relative inline-flex shrink-0 ${!isCollapsed ? 'mr-3' : ''}`}
                  >
                    <IconComponent
                      className={
                        isHighlight && !isActive
                          ? 'text-[#A3E635]/70 group-hover:text-[#A3E635]'
                          : ''
                      }
                      size={22}
                      strokeWidth={1.5}
                    />
                    {badgeCount > 0 && (
                      <span
                        className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#A3E635] px-1 text-[9px] font-bold leading-none text-black shadow-[0_0_10px_rgba(163,230,53,0.6)]"
                        aria-label={`${badgeCount} unread conversations`}
                      >
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    )}
                  </span>

                  {!isCollapsed && (
                    <span className="text-sm font-medium">
                      {item.label}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Account */}
      <div className="border-t border-white/[0.04] p-3">
        {renderAccountSection()}
      </div>

      {canAccessCreatorTools && postCreatorId && (
        <PostComposer
          open={postOpen}
          onClose={() => setPostOpen(false)}
          creatorId={postCreatorId}
          onPosted={handlePosted}
        />
      )}
    </div>
  );
};

export default VerticalNavbar;